/**
 * Real parallel-step scheduling — computes when each preparation step
 * actually starts/finishes, and which Timeline row it can share with
 * others, given that some steps don't need the preparer's full
 * attention for their whole duration (see PreparationStep.attentionDuration's
 * doc comment).
 *
 * There's still exactly one preparer (one person doing the recipe), so
 * steps are still taken up — started — strictly one at a time; what
 * changes is that starting a step doesn't necessarily tie the preparer
 * up for its whole duration any more. A step with no attentionDuration
 * set behaves exactly as before (occupies the preparer for its entire
 * duration) — so a recipe with nothing newly annotated schedules
 * identically to the old cursor-sum this replaces.
 *
 * Deliberately independent of RecipeStore/DOM — a pure function over
 * `PreparationStep[]` (already stepOrder-sorted, already grouped
 * consecutively by node — exactly buildPreparationSteps()'s own output),
 * same spirit as stepOrder.ts: easy to verify standalone, reusable from
 * both computeNodeTotalDuration() and TimelineView.
 */
import type { PreparationStep } from './RecipeTextRenderer.js';

export interface ScheduledStep {
  step: PreparationStep;
  startSeconds: number;
  /** startSeconds + the step's own full duration — when its output is actually ready, regardless of how little attention it needed. */
  finishSeconds: number;
  /** Greedy-assigned so no two steps sharing a row ever overlap in [startSeconds, finishSeconds) — see assignRows() below. */
  row: number;
}

/**
 * Walks `steps` in their given (stepOrder, i.e. topological) order,
 * maintaining one global "preparer free at" cursor — there's one
 * preparer — and one `finishAt` map keyed by node id. Because `steps`
 * is topologically sorted, every one of a node's children is already
 * finished-and-recorded by the time that node's own steps are reached.
 *
 * A node's own later transformation usages are implicitly chained to
 * its earlier ones (confirmed design: a node's transformations are
 * causally sequential, never independently reorderable) — modeled here
 * as one more readiness dependency, not a special case: the previous
 * step belonging to the SAME node must also have finished.
 */
export function computeSchedule(steps: PreparationStep[]): ScheduledStep[] {
  const finishAtByNode = new Map<string, number>();
  let preparerFreeAt = 0;

  const scheduled: ScheduledStep[] = steps.map((step) => {
    const children = step.node.data.children ?? [];
    let depsReady = 0;
    for (const childId of children) {
      const childFinish = finishAtByNode.get(childId);
      if (childFinish != null) depsReady = Math.max(depsReady, childFinish);
    }
    // This node's own previous usage, if any — same readiness rule as a
    // real dependency, see this function's own doc comment.
    const ownPrevFinish = finishAtByNode.get(step.node.id);
    if (ownPrevFinish != null) depsReady = Math.max(depsReady, ownPrevFinish);

    const startSeconds = Math.max(preparerFreeAt, depsReady);
    const duration = step.duration ?? 0;
    const finishSeconds = startSeconds + duration;
    // Clamp defensively — the editing UI already constrains this, but a
    // stale/hand-edited value should never be able to free the preparer
    // LATER than the step's own finish, nor hold them past it.
    const attention = Math.min(step.attentionDuration ?? duration, duration);

    preparerFreeAt = startSeconds + attention;
    finishAtByNode.set(step.node.id, finishSeconds);

    return { step, startSeconds, finishSeconds, row: 0 };
  });

  assignRows(scheduled);
  return scheduled;
}

/**
 * Classic "minimum meeting rooms" greedy interval-scheduling: process in
 * start-time order, reuse the first row whose last-assigned step has
 * already finished by this one's start, else open a new row. Mutates
 * `row` on each entry in place; the caller's own (stepOrder) array order
 * is untouched — only this local sorted copy's iteration order differs.
 */
function assignRows(scheduled: ScheduledStep[]): void {
  const byStart = [...scheduled].sort((a, b) => a.startSeconds - b.startSeconds);
  const rowFreeAt: number[] = [];
  for (const entry of byStart) {
    let row = rowFreeAt.findIndex((freeAt) => freeAt <= entry.startSeconds);
    if (row === -1) {
      row = rowFreeAt.length;
      rowFreeAt.push(entry.finishSeconds);
    } else {
      rowFreeAt[row] = entry.finishSeconds;
    }
    entry.row = row;
  }
}
